import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import type { StageAnalyticsSection, StageTimeRow } from "@/types/invites"

/**
 * OKS-956 — « Temps par étape » : p50 / p90 days per pipeline stage.
 *
 * Renders under the funnel activity chart on `/candidates`, fed by the
 * `stage_analytics` deferred prop (`BrandOps::CandidateStageAnalytics`).
 *
 * # The server owns the row set — this component is a renderer
 *
 * No client-side sorting, no stage list, no threshold, no `—` placeholder
 * rows. Which stages appear, in what order, and whether a stage is
 * trustworthy at all are all decided server-side (`RENDERED_STAGES` +
 * `PRIORITY_STAGES`). Re-deriving any of it here would create a second home
 * for the decision, and the two would drift silently — the failure this
 * table exists to avoid, not to reproduce.
 *
 * # Only COMPLETED stays count
 *
 * The subtitle discloses a rule a reader cannot otherwise infer: a candidate
 * still sitting in a stage appears nowhere. An open-ended stay is censored
 * data, and counting it would report a figure that improves as the pipeline
 * gets worse.
 *
 * A brand may therefore legitimately see fewer than the full set of rows —
 * a stage with no completed stays renders no row, not a zero and not a dash
 * (OKS-982). A partial set is normal, never an error.
 *
 * # A11y
 *
 * A real `<table>` with `scope="col"` headers and an accessible name. The
 * priority stage's accent is never the sole signal — it is also first, and
 * every value is text.
 *
 * ⚠️ **This is the SECOND table on `/candidates`** — the candidate list is the
 * other. It contributes `table`, `row`, `cell`, `columnheader` and `rowgroup`
 * roles to that page's accessibility tree, so an unscoped `getByRole("row")`
 * or `getByRole("table")` there now matches both. Nothing queries those roles
 * today (swept at OKS-956: zero role-based table locators across
 * `tests/playwright/` and `app/frontend/`, against a control of 48 files using
 * `getByRole`), so this is a note for whoever adds one — scope it to
 * `getByTestId("stage-time-table")` or to the list's own testid. A locator
 * that silently matches two tables fails as if the author's own selector were
 * wrong.
 */

/**
 * Formats a day count for display.
 *
 * Locale-aware (`12,4 j` in FR) and integers stay integers (`29 j`, never
 * `29,0 j`). The service already rounds to one decimal; this only formats.
 *
 * @param value  Days, or `null` when the percentile is undefined.
 * @param locale BCP-47 tag from i18next.
 * @return       Formatted number, or `null` to signal "nothing to render".
 */
function formatDays(value: number | null, locale: string): string | null {
  if (value === null || value === undefined) return null

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    value,
  )
}

interface StageTimeTableProps {
  section: StageAnalyticsSection
  /** Partial reload of this prop alone. Writes no URL (OKS-788/849). */
  onRetry?: () => void
  className?: string
}

export function StageTimeTable({
  section,
  onRetry,
  className,
}: StageTimeTableProps) {
  const { t, i18n } = useTranslation()

  const title = t("invites.funnel.time_in_stage.title")

  const days = (value: number | null) => {
    const formatted = formatDays(value, i18n.language)
    if (formatted === null) return null

    return t("invites.funnel.time_in_stage.unit_days", { count: formatted })
  }

  return (
    <Card className={cn(className)} data-testid="stage-time-table">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>
          {t("invites.funnel.time_in_stage.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent data-state={section.state}>
        {section.state === "error" && (
          <div className="flex flex-col items-start gap-3">
            <p className="text-muted-foreground text-sm">
              {t("candidates.list.error.description")}
            </p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t("candidates.list.error.retry")}
              </Button>
            )}
          </div>
        )}

        {/* No header row over an empty table — that reads as a failed load. */}
        {section.state === "empty" && (
          <p className="text-muted-foreground text-sm">
            {t("invites.funnel.time_in_stage.empty")}
          </p>
        )}

        {section.state === "populated" && (
          <Table aria-label={title}>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">
                  {t("invites.funnel.time_in_stage.col_stage")}
                </TableHead>
                <TableHead scope="col" className="text-right">
                  {t("invites.funnel.time_in_stage.col_p50")}
                </TableHead>
                <TableHead scope="col" className="text-right">
                  {t("invites.funnel.time_in_stage.col_p90")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {section.rows.map((row: StageTimeRow, index: number) => (
                <TableRow
                  key={row.stage}
                  data-stage={row.stage}
                  data-priority={index === 0 ? "true" : "false"}
                >
                  <TableCell
                    className={cn(
                      "font-medium",
                      // Accent on the leading row. Position carries the same
                      // meaning, so colour is never the sole signal.
                      index === 0 && "text-primary",
                    )}
                  >
                    {t(`candidates.status.${row.stage}`)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {days(row.p50_days)}
                  </TableCell>
                  {/*
                    Below `sm` the p90 cell stacks under the stage name rather
                    than forcing a horizontal scroll (UX-Spec-953 §5).
                  */}
                  <TableCell className="text-right tabular-nums">
                    {days(row.p90_days)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Deferred-pending state. Shaped like the populated table — caption bar plus
 * six rows of three — so the swap on resolve does not jump the layout.
 *
 * OKS-982 — raised from three rows to six when the three suppressed stages
 * were re-admitted. No single count is right, since the resolved set varies
 * from three to six rows; six is the steady state of an active brand, and
 * sizing to it means the common case does not push page content down on
 * resolve (UX Spec §3, §9.2).
 *
 * Exported alongside `StageTimeTable` so `<Deferred fallback>` renders the
 * same component family as the resolved branch (the OKS-923 lesson).
 */
export function StageTimeTableSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn(className)} data-testid="stage-time-table-skeleton">
      <CardHeader>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-2 h-3 w-64" />
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <div
              key={row}
              data-testid="stage-time-table-skeleton-row"
              className="flex items-center justify-between gap-4"
            >
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
